import { useCallback, useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { calendarApi, timeEntriesApi } from '@/api/endpoints'
import type { CalendarResponse, TimeEntry } from '@/api/types'
import EditEntryModal from '@/components/EditEntryModal'
import { useTimerStore } from '@/stores/useTimerStore'
import { Button, Card } from '@/components/ui'
import { errorMessage } from '@/utils/errors'
import { formatDuration, formatHours, formatTime } from '@/utils/format'

dayjs.extend(isoWeek)

const DATE = 'YYYY-MM-DD'

export default function CalendarPage() {
  const { running, refresh, start } = useTimerStore()
  const [weekStart, setWeekStart] = useState(() => dayjs().startOf('isoWeek'))
  const [data, setData] = useState<CalendarResponse | null>(null)
  const [editing, setEditing] = useState<TimeEntry | null>(null)
  const [error, setError] = useState<string | null>(null)

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => weekStart.add(i, 'day')), [weekStart])

  const load = useCallback(() => {
    const from = weekStart.format(DATE)
    const to = weekStart.add(6, 'day').format(DATE)
    return calendarApi.range(from, to).then(setData)
  }, [weekStart])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const byDate = useMemo(() => {
    const map: Record<string, CalendarResponse['days'][number]> = {}
    data?.days.forEach((d) => (map[d.date] = d))
    return map
  }, [data])

  const byId = useMemo(() => {
    const map: Record<string, TimeEntry> = {}
    data?.days.forEach((d) => d.entries.forEach((e) => (map[e.id] = e)))
    return map
  }, [data])

  // Pokračování je stejné jako na Dnešku: nové stopky se stejným projektem
  // i popisem, a jen když nic neběží — jinak by kliknutí tiše zastavilo
  // rozdělanou práci. Nové měření začíná teď, ne v den, odkud se kliklo.
  const onResume = async (entry: TimeEntry) => {
    await start(entry.projectId, entry.description ?? undefined)
    await load()
  }

  /**
   * Přetažení záznamu na jiný den. Mění se jen datum — denní čas i délka
   * zůstávají, takže se ranní hodina neposune na noc. Přetahování jinam se
   * počítá odsud, ne z prohlížeče: `data` má záznam ještě před přesunem.
   */
  const onMove = async (entryId: string, targetDate: string) => {
    const entry = byId[entryId]
    if (!entry) return

    const from = dayjs(entry.startTime)
    const to = dayjs(targetDate).hour(from.hour()).minute(from.minute()).second(from.second()).millisecond(0)
    if (to.isSame(from)) return

    setError(null)
    try {
      await timeEntriesApi.update(entry.id, {
        startTime: to.toISOString(),
        // Běžícímu záznamu konec neposíláme, ať ho omylem nezastavíme.
        ...(entry.endTime && entry.durationSecs != null
          ? { endTime: to.add(entry.durationSecs, 'second').toISOString() }
          : {}),
      })
      await load()
    } catch (err) {
      setError(errorMessage(err, 'Záznam se nepodařilo přesunout.'))
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-lg font-semibold">Kalendář</h1>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" onClick={() => setWeekStart(weekStart.subtract(1, 'week'))}>
            ‹ Předchozí
          </Button>
          <Button variant="secondary" onClick={() => setWeekStart(dayjs().startOf('isoWeek'))}>
            Tento týden
          </Button>
          <Button variant="secondary" onClick={() => setWeekStart(weekStart.add(1, 'week'))}>
            Další ›
          </Button>
          <span className="text-sm text-slate-500 sm:ml-3">Celkem {formatHours(data?.totalSecs ?? 0)}</span>
        </div>
      </div>

      {error && (
        <Card className="border-red-200 bg-red-50">
          <p className="text-sm text-red-700">{error}</p>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-7">
        {days.map((d) => {
          const key = d.format(DATE)
          const day = byDate[key]
          return (
            <DayCell
              key={key}
              date={d}
              entries={day?.entries ?? []}
              totalSecs={day?.totalSecs ?? 0}
              canResume={!running}
              onEdit={setEditing}
              onResume={onResume}
              onMove={onMove}
            />
          )
        })}
      </div>

      {editing && (
        <EditEntryModal
          entry={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null)
            await load()
          }}
        />
      )}
    </div>
  )
}

/** Jeden den v týdnu — zároveň cíl přetažení záznamu z jiného dne. */
function DayCell({
  date,
  entries,
  totalSecs,
  canResume,
  onEdit,
  onResume,
  onMove,
}: {
  date: dayjs.Dayjs
  entries: TimeEntry[]
  totalSecs: number
  canResume: boolean
  onEdit: (entry: TimeEntry) => void
  onResume: (entry: TimeEntry) => void
  onMove: (entryId: string, targetDate: string) => void
}) {
  const key = date.format(DATE)
  const isToday = date.isSame(dayjs(), 'day')
  const [over, setOver] = useState(false)

  return (
    <Card
      className={`min-h-40 ${isToday ? 'ring-2 ring-brand-500' : ''} ${
        over ? 'bg-brand-50 ring-2 ring-brand-400' : ''
      }`}
      // Bez preventDefault v dragOver prohlížeč puštění vůbec nenabídne.
      onDragOver={(e) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        setOver(true)
      }}
      // dragleave chodí i při přejetí na vnořený prvek — zvýraznění zhasneme,
      // až když kurzor opustí celou kartu, jinak by blikalo nad každým záznamem.
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false)
      }}
      onDrop={(e) => {
        e.preventDefault()
        setOver(false)
        const id = e.dataTransfer.getData('text/plain')
        if (id) onMove(id, key)
      }}
    >
      <div className="mb-2 flex items-baseline justify-between">
        <span className="text-xs font-semibold uppercase text-slate-500">{date.format('dd')}</span>
        <span className="text-xs text-slate-400">{date.format('D.M.')}</span>
      </div>
      {entries.length === 0 ? (
        <p className="text-xs text-slate-300">—</p>
      ) : (
        <>
          <div className="mb-2 text-sm font-medium text-brand-700">{formatHours(totalSecs)}</div>
          <ul className="space-y-1">
            {entries.map((e) => (
              <EntryChip key={e.id} entry={e} canResume={canResume} onEdit={onEdit} onResume={onResume} />
            ))}
          </ul>
        </>
      )}
    </Card>
  )
}

/**
 * Záznam v kalendáři. Název projektu ani popis se nezkracují — v úzkém sloupci
 * se radši zalomí, než aby se z "Migrace fakturace na Fakturoid" stalo "Migra…"
 * a člověk musel otevírat detail, aby zjistil, co to bylo.
 */
function EntryChip({
  entry,
  canResume,
  onEdit,
  onResume,
}: {
  entry: TimeEntry
  canResume: boolean
  onEdit: (entry: TimeEntry) => void
  onResume: (entry: TimeEntry) => void
}) {
  // Běžící záznam se přetahovat nedá: nemá konec a přesunout rozdělané měření
  // do minulého dne nedává smysl.
  const draggable = !entry.running

  return (
    <li
      draggable={draggable}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', entry.id)
        e.dataTransfer.effectAllowed = 'move'
      }}
      className={`rounded bg-slate-50 hover:bg-slate-100 ${draggable ? 'cursor-grab active:cursor-grabbing' : ''}`}
    >
      {/* Tlačítko "pokračovat" je schválně vedle, ne uvnitř — tlačítko v tlačítku
          není platné HTML a klik by se pral s otevřením detailu. */}
      <button
        type="button"
        title={draggable ? 'Upravit záznam (přetažením se přesune na jiný den)' : 'Upravit záznam'}
        onClick={() => onEdit(entry)}
        className="block w-full px-2 py-1 text-left text-xs"
      >
        <div className="break-words font-medium text-slate-700">{entry.projectName}</div>
        <div className="break-words text-slate-500">{entry.clientName}</div>
        {entry.description && <div className="break-words text-slate-500">{entry.description}</div>}
        <div className="mt-0.5 flex justify-between text-slate-400">
          <span>{formatTime(entry.startTime)}</span>
          <span className="font-mono">{entry.durationSecs != null ? formatDuration(entry.durationSecs) : 'běží'}</span>
        </div>
      </button>
      {canResume && entry.endTime && (
        <button
          type="button"
          title="Znovu spustit stopky na stejném projektu"
          onClick={() => onResume(entry)}
          className="block w-full rounded-b px-2 pb-1 text-left text-xs text-brand-600 hover:underline"
        >
          pokračovat
        </button>
      )}
    </li>
  )
}
